import { config } from '../config';
import { compareConfirmedFirst } from './order';
import type { RankedByStatus } from './order';
import type { StatusOverride } from '../types';

/**
 * HT-5 — **the halftime adjustment list** (build unit U-13), as three pure functions.
 *
 * §7.3 states four separate rules, and each one is a place a plausible implementation goes wrong:
 * eligibility, ordering, a **bound**, and a snapshot/re-entry rule that is easy to get *almost*
 * right. They live here rather than in the screen because §7.3's snapshot rule is the part VER-8
 * drives behaviourally, and a rule expressed as `useEffect` dependencies is not a rule anyone can
 * read back.
 *
 * ── Eligibility, and why the exclusion is the requirement ────────────────────
 *
 * > *"Hypotheses on the match with `statusOverride === null`. A hypothesis the user has just
 * > **disproved or invalidated is excluded** — reading out a pattern you decided was wrong, minutes
 * > after deciding it, is the worst possible output of this screen, and a `disproved` hypothesis
 * > with count 6 would otherwise rank near the top."*
 *
 * One predicate covers both exclusions because `StatusOverride` **is** the dismissal dimension
 * (§3.3): `disproved` and `invalidated` are its only non-null values. Testing
 * `status !== 'disproved'` instead would read the materialized four-valued enum, which is derived
 * from the count — and would then silently miss `invalidated`.
 *
 * A hypothesis created *at* halftime has `cachedCount === 0` and ranks last, but **is eligible**
 * (§19 F-28): *"a flag promoted at halftime represents a first-half moment, and excluding it would
 * make FLAG-3 and HT-5 mutually pointless, which HT-7 forbids."*
 *
 * ── Ordering ────────────────────────────────────────────────────────────────
 *
 * > *"`confirmed` first, then `cachedCount` desc, then the total tiebreak chain of §6.1."*
 *
 * All three terms **are** `compareConfirmedFirst`, imported rather than restated. §6.1's chain ends
 * at `id` precisely so the order is total, and a second copy of it here would be a second chance for
 * LOCK-2's and DISC-4's determinism promise to drift — the same argument `order.ts` makes for
 * exporting `compareDiscovery` to `lock.ts` in the first place. **§10.2's distillation list asks for
 * the identical order** (U-14b), so the comparator now has two consumers and one home; what differs
 * between the two surfaces is the *filter* below, which is why the filter stayed here.
 *
 * `confirmed` is read from the **materialized** `status`, and that is correct here in a way it is
 * **not** on the locked board: INV-11/LIVE-12 forbid count-driven reflow of *tiles*, which is why
 * `lockedOrder` cannot see a count. This list is not the board, it is recomputed only on entry
 * (below), and HT-5 asks for confirmed-first in those words.
 *
 * ── The snapshot rule, which is the part with a wrong answer that looks right ─
 *
 * > *"The order is computed **on entry to the adjustments surface** and held for that surface's
 * > visible lifetime. While it is open: rows already listed keep their positions; hypotheses created
 * > meanwhile **append at the tail**; status changes **restyle in place** and do not remove a row.
 * > **Leaving and re-entering the surface recomputes from scratch** … Snapshotting once at halftime
 * > entry and holding it for the whole break would make a flag promoted at halftime invisible in the
 * > adjustment list — a behavioral violation of HT-7's independence guarantee."*
 *
 * Three distinct populations follow, and telling them apart is why {@link AdjustmentSnapshot}
 * records `knownIds` and not merely the listed order:
 *
 * | At entry the row was… | …and now | Displayed? |
 * |---|---|---|
 * | listed | still there, any status | **yes, at its original index** — "restyle in place, do not remove" |
 * | listed | deleted | no — there is nothing to restyle |
 * | present but ineligible (dismissed) | override cleared | **no** — §7.3: *"clearing the override returns them to the list **on the next entry**"* |
 * | absent entirely (created meanwhile) | eligible | **yes, appended at the tail** |
 *
 * Without `knownIds` the third and fourth rows are indistinguishable — both are "an eligible id that
 * is not in the listed order" — and an implementation that appends both looks correct in every test
 * that does not clear an override while the surface is open.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Inputs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What HT-5 ranking may see. Structurally minimal, in `order.ts`'s tradition and for its reason:
 * the function body can read exactly these fields, so a later edit cannot quietly start ranking on
 * `boardPosition` (which would launder the board's composition into a read-aloud list) or on
 * `lastIncrementTs` (which would make the order move under the user between renders).
 */
export interface AdjustmentCandidate extends RankedByStatus {
  readonly id: string;
  readonly text: string;
  /** §3.3's dismissal dimension — the whole of HT-5's eligibility test. */
  readonly statusOverride: StatusOverride;
}

/**
 * What the surface captured **on entry**. Opaque to the caller beyond being handed back unchanged.
 *
 * `listedIds` is the frozen order; `knownIds` is every hypothesis that existed at that moment,
 * eligible or not. See the table above for why both are needed.
 */
export interface AdjustmentSnapshot {
  readonly listedIds: readonly string[];
  readonly knownIds: ReadonlySet<string>;
}

// ─────────────────────────────────────────────────────────────────────────────
// HT-5's three rules
// ─────────────────────────────────────────────────────────────────────────────

/**
 * §7.3 — *"Hypotheses on the match with `statusOverride === null`."*
 *
 * Exported so the screen never re-expresses it. The negative it carries — a `disproved` or
 * `invalidated` hypothesis is **absent**, not merely styled differently — is what VER-8's last
 * assertion drives.
 */
export function isAdjustmentEligible(candidate: {
  readonly statusOverride: StatusOverride;
}): boolean {
  return candidate.statusOverride === null;
}

/**
 * §7.3 — `confirmed` first, then §6.1's chain.
 *
 * Returns a new array; the input is never sorted in place, for the reason `discoveryOrder` gives.
 * Ineligible candidates are **filtered out here**, so a caller cannot rank a dismissed hypothesis
 * by forgetting a filter at the call site.
 */
export function rankAdjustments<T extends AdjustmentCandidate>(candidates: readonly T[]): T[] {
  return candidates.filter(isAdjustmentEligible).sort(compareConfirmedFirst);
}

/**
 * §7.3 — the snapshot taken **on entry to the adjustments surface**.
 *
 * `knownIds` spans every candidate handed in, *including* the ineligible ones, which is the whole
 * reason this takes the unfiltered list rather than the ranked one.
 */
export function snapshotAdjustments(
  candidates: readonly AdjustmentCandidate[],
): AdjustmentSnapshot {
  return {
    listedIds: rankAdjustments(candidates).map((candidate) => candidate.id),
    knownIds: new Set(candidates.map((candidate) => candidate.id)),
  };
}

/**
 * §7.3 — what the surface renders on any render *after* entry, given the snapshot it took.
 *
 * The snapshot's order is authoritative for everything it listed; only genuinely new hypotheses are
 * appended, and they are appended **in ranked order among themselves** so that promoting two flags
 * in a row does not produce an arbitrary pair ordering.
 *
 * Note what this function deliberately cannot do: it never re-sorts `listedIds`. A row whose count
 * changed, or whose status flipped to `confirmed`, keeps its index — *"status changes restyle in
 * place"* — because the row it returns is the **current** row read from `candidates`, at the **old**
 * position.
 */
export function applyAdjustmentSnapshot<T extends AdjustmentCandidate>(
  snapshot: AdjustmentSnapshot,
  candidates: readonly T[],
): T[] {
  const byId = new Map(candidates.map((candidate) => [candidate.id, candidate]));

  // Listed at entry and still present — kept at its original index whatever its status now is.
  const held: T[] = [];
  for (const id of snapshot.listedIds) {
    const candidate = byId.get(id);
    if (candidate !== undefined) held.push(candidate);
  }

  // Created since entry. `knownIds` is what separates these from a dismissal the user has just
  // cleared, which §7.3 holds back until the next entry.
  const appended = rankAdjustments(
    candidates.filter((candidate) => !snapshot.knownIds.has(candidate.id)),
  );

  return [...held, ...appended];
}

/**
 * §7.3 — *"The list shows the top `HT_ADJUSTMENT_CANDIDATES = 8` with a *Show all* control below.
 * At ≥ 28 pt an unbounded list would push the sub-activity cards off a 390×844 screen and turn a
 * glanceable ranking into a scroll."*
 *
 * A view concern expressed here rather than as a `.slice(0, 8)` at the call site, so NFR-9's "no
 * magic numbers at call sites" holds and the bound is testable without a DOM. `HT_ADJUSTMENT_CANDIDATES`
 * is a §1.3 constant that already exists (`config/constants.ts`), Zod-validated, and cross-checked
 * against `BOARD_CAP` in `config/validate.ts` — no new constant is introduced by this unit.
 */
export function boundAdjustments<T>(ordered: readonly T[], showAll: boolean): T[] {
  return showAll ? [...ordered] : ordered.slice(0, config.HT_ADJUSTMENT_CANDIDATES);
}

/**
 * INV-6 at halftime — *"`phase=halftime` rows unconstrained from 0 to 3"* (§7.3, §17.6).
 *
 * `repos/talkPoints.writeSelection` already refuses a selection above `TALKPOINT_COUNT`; this is the
 * *view's* half of the same bound, so the promote control is disabled before a rejection can happen
 * rather than after. Two expressions of one rule, and they cannot disagree: both read the constant.
 *
 * **Zero is legal and there is no exit nag** (§7.3, HT-1) — hence a `canPromote`, never a `complete`.
 */
export function canPromoteAnother(promotedCount: number): boolean {
  return promotedCount < config.TALKPOINT_COUNT;
}
